import React, { useMemo, useEffect, useState } from 'react';
import { Checkbox } from './checkbox';
import { Badge } from './badge';
import { Button } from './button';
import { Input } from './input';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { ChevronDown, X } from 'lucide-react';

type Option = { id: string; name: string };

interface MultiSelectTagsProps {
  label: string;
  value: string[];
  onChange: (next: string[]) => void;
  options: Option[];
  placeholder?: string;
  className?: string;
  /** When true, shows a search field to filter options in the dropdown */
  filterable?: boolean;
  searchPlaceholder?: string;
  noResultsText?: string;
  /** Enable when rendered inside a Dialog so the dropdown list can scroll */
  modal?: boolean;
  /** When true, user can add a custom option from the search text */
  allowCreate?: boolean;
  /** Return the option to add, or null to reject the custom label */
  onCreateOption?: (label: string) => Option | null;
  createOptionText?: (label: string) => string;
}

export function MultiSelectTags({
  label,
  value,
  onChange,
  options,
  placeholder,
  className,
  filterable,
  searchPlaceholder,
  noResultsText,
  modal = false,
  allowCreate = false,
  onCreateOption,
  createOptionText,
}: MultiSelectTagsProps) {
  const [open, setOpen] = React.useState(false);
  const [filterText, setFilterText] = useState('');

  const selected = useMemo(() => new Set(value), [value]);
  const toggle = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange(Array.from(next));
  };

  const trimmedFilter = filterText.trim();

  const tryCreateOption = (label: string) => {
    if (!allowCreate || !onCreateOption) return;
    const created = onCreateOption(label);
    if (!created || selected.has(created.id)) return;
    onChange([...value, created.id]);
    setFilterText('');
  };

  const filteredOptions = useMemo(() => {
    if (!filterable || !trimmedFilter) {
      return options;
    }
    const q = trimmedFilter.toLowerCase();
    return options.filter((o) => o.name.toLowerCase().includes(q));
  }, [options, filterable, trimmedFilter]);

  const canCreate =
    allowCreate &&
    !!trimmedFilter &&
    !options.some(
      (option) =>
        option.id === trimmedFilter ||
        option.name.toLowerCase() === trimmedFilter.toLowerCase(),
    );

  useEffect(() => {
    if (!open) {
      setFilterText('');
    }
  }, [open]);

  return (
    <div className={`flex flex-col gap-y-1 ${className || ''}`}>
      <div className="flex flex-wrap gap-2">
        {value.map((id) => {
          const opt = options.find((o) => o.id === id);
          return (
            <Badge key={id} className="mt-2 flex items-center gap-1 pr-1" variant="secondary">
              <span>{opt?.name || id}</span>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  toggle(id);
                }}
                className="ml-1 hover:bg-muted-foreground/20 dark:hover:bg-muted-foreground/30 rounded-full p-0.5 transition-colors"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          );
        })}
      </div>
      <Popover open={open} onOpenChange={setOpen} modal={modal}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            className="flex items-center justify-between gap-2 text-sm text-muted-foreground font-normal hover:text-muted-foreground hover:bg-transparent"
          >
            <span>{placeholder}</span>
            <ChevronDown className="h-4 w-4 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80 p-2" align="start">
          {filterable && (
            <Input
              type="search"
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && canCreate) {
                  e.preventDefault();
                  tryCreateOption(trimmedFilter);
                }
              }}
              placeholder={searchPlaceholder}
              className="mb-2 h-8 text-sm"
              aria-label={searchPlaceholder || label}
            />
          )}
          <div className="max-h-64 overflow-auto">
            {canCreate && (
              <button
                type="button"
                className="flex w-full items-center rounded p-2 text-left text-sm hover:bg-accent dark:hover:bg-accent/50"
                onClick={() => tryCreateOption(trimmedFilter)}
              >
                {createOptionText?.(trimmedFilter) ?? `Add "${trimmedFilter}"`}
              </button>
            )}
            {filteredOptions.length === 0 && !canCreate ? (
              <p className="text-sm text-muted-foreground px-2 py-1">{noResultsText ?? '-'}</p>
            ) : (
              filteredOptions.map((opt) => (
                <label
                  key={opt.id}
                  className="flex items-center gap-2 p-2 rounded hover:bg-accent dark:hover:bg-accent/50"
                >
                  <Checkbox checked={selected.has(opt.id)} onCheckedChange={() => toggle(opt.id)} />
                  <span className="text-sm/1">{opt.name}</span>
                </label>
              ))
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
